import React, { useEffect, useState } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { CheckIcon } from './DepartmentIcons';
import { useToast } from '@/hooks/useToast';

export const DepartmentSurveysView: React.FC = () => {
  const { notify } = useToast();
  const [surveys, setSurveys] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [title, setTitle] = useState<string>('');
  const [audience, setAudience] = useState<string>('Докторанты 1–3 курса');
  const [deadline, setDeadline] = useState<string>('2026-08-25');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const [surveysRes, templatesRes] = await Promise.all([
        departmentApi.surveys(),
        departmentApi.surveyTemplates(),
      ]);
      setSurveys(surveysRes);
      setTemplates(templatesRes);
    } catch {
      setSurveys([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleCreateSurvey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setSubmitting(true);
      await departmentApi.createSurvey({
        title: title.trim(),
        target_audience: audience,
        deadline,
      });
      setTitle('');
      setToastMessage('Запрос опубликован');
      setTimeout(() => setToastMessage(null), 3500);
      fetchSurveys();
    } catch {
      notify('Ошибка при публикации запроса', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSurvey = async (surveyId: string) => {
    try {
      await departmentApi.closeSurvey(surveyId);
      fetchSurveys();
    } catch {
      notify('Не удалось закрыть запрос', 'error');
    }
  };

  const handleApplyTemplate = (tpl: any) => {
    setTitle(tpl.title);
    setAudience(tpl.target_audience || 'Докторанты 1–3 курса');
  };

  const activeSurveys = surveys.filter((s) => !s.is_closed);
  const totalAnswers = surveys.reduce((acc, s) => acc + (s.responses_count || 0), 0);
  const totalWaiting = activeSurveys.reduce(
    (acc, s) => acc + Math.max(0, (s.total_target || 12) - (s.responses_count || 0)),
    0
  );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-6">
        <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
          Information Requests
        </span>
        <h2 className="text-2xl font-bold text-white tracking-tight mt-1">
          Опросы и запросы
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-4xl">
          Собирайте актуальные кафедральные сведения без переписки в сторонних формах. Ответы агрегируются в одном месте.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Активные</span>
            <div className="text-3xl font-extrabold text-white mt-1">{activeSurveys.length}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Ответов</span>
            <div className="text-3xl font-extrabold text-cyan-400 mt-1">{totalAnswers}</div>
          </div>
          <div className="p-4 rounded-xl border border-amber-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Ожидают</span>
            <div className="text-3xl font-extrabold text-amber-400 mt-1">{totalWaiting}</div>
          </div>
          <div className="p-4 rounded-xl border border-cyan-500/20 bg-[#061420]/80">
            <span className="text-[11px] font-semibold uppercase text-slate-400">Шаблоны</span>
            <div className="text-3xl font-extrabold text-white mt-1">{templates.length || 4}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">Создать запрос</h3>
          <p className="text-xs text-slate-400 mt-0.5">Опрос или сбор одного показателя.</p>

          <form onSubmit={handleCreateSurvey} className="mt-4 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Название
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Что нужно уточнить?"
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Аудитория
              </label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              >
                <option value="Докторанты 1–3 курса">Докторанты 1–3 курса</option>
                <option value="Докторанты 1 курса">Докторанты 1 курса</option>
                <option value="Докторанты 3 курса">Докторанты 3 курса</option>
                <option value="Сотрудники кафедры">Сотрудники кафедры</option>
                <option value="Научные руководители">Научные руководители</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400 block mb-1">
                Срок
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full bg-[#061420] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="w-full py-2.5 px-4 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold text-xs transition-all shadow active:scale-95 disabled:opacity-50"
            >
              {submitting ? 'Публикация...' : 'Опубликовать запрос'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-7 rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
          <h3 className="text-sm font-bold text-white">Активные опросы</h3>
          <p className="text-xs text-slate-400 mt-0.5">Прогресс ответов в реальном рабочем списке.</p>

          <div className="mt-4 space-y-3">
            {activeSurveys.map((survey) => {
              const progressPct = survey.total_target
                ? Math.round((survey.responses_count / survey.total_target) * 100)
                : 0;

              return (
                <div
                  key={survey.id}
                  className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 pr-2">
                      <h4 className="text-xs font-bold text-white truncate">{survey.title}</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {survey.target_audience} · до {survey.deadline}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] font-mono text-slate-400">
                        {survey.responses_count}/{survey.total_target}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCloseSurvey(survey.id)}
                        className="px-3 py-1 rounded-lg border border-slate-700 bg-slate-800/80 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-all active:scale-95"
                      >
                        Закрыть
                      </button>
                    </div>
                  </div>

                  <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-500/20 bg-[#091b29]/80 p-5">
        <h3 className="text-sm font-bold text-white">Готовые шаблоны</h3>
        <p className="text-xs text-slate-400 mt-0.5">Частые служебные запросы кафедры.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {templates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => handleApplyTemplate(tpl)}
              className="p-4 rounded-xl bg-[#061420] border border-slate-800 hover:border-cyan-500/40 text-left transition-all group"
            >
              <h4 className="text-xs font-bold text-white group-hover:text-cyan-300 transition-colors">
                {tpl.title}
              </h4>
              <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{tpl.subtitle}</p>
            </button>
          ))}
        </div>
      </div>

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#081a27] border border-cyan-500/40 text-cyan-300 text-xs font-semibold shadow-2xl animate-fade-in">
          <CheckIcon className="w-4 h-4 text-cyan-400" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
